import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import VerificarStatusClicksign from './VerificarStatusClicksign';
import PropostasEnviadasDisplay from './PropostasEnviadasDisplay';

interface PropostaStatusDisplayProps {
  userEmail: string;
  onAvancar: () => void;
}

export default function PropostaStatusDisplay({ userEmail, onAvancar }: PropostaStatusDisplayProps) {
  const [loading, setLoading] = useState(true);
  const [statusProposta, setStatusProposta] = useState<'pendente' | 'assinado' | 'nao_encontrado'>('nao_encontrado');
  const [contratacaoId, setContratacaoId] = useState<string>('');
  const { toast } = useToast();

  // Função auxiliar para verificar se está assinado
  const isAssinado = (value: any): boolean => {
    return value === 1 || value === true || value === '1' || value === 'true' || value === 'assinado';
  };

  useEffect(() => {
    if (userEmail) {
      buscarContratacaoEVerificarProposta();
    }
  }, [userEmail]);

  const buscarContratacaoEVerificarProposta = async () => {
    try {
      setLoading(true);
      console.log('🔍 Iniciando busca por proposta para email:', userEmail);
      
      // Primeiro, extrair o CPF do email/usuário atual
      const { data: { user } } = await supabase.auth.getUser();
      console.log('📊 Usuário autenticado:', user?.email);
      
      // Buscar diretamente na tabela contratacao_contratos usando o email para filtrar
      // Primeiro, vamos buscar todas as contratações relacionadas ao email
      const { data: contratacoes, error: contratacaoError } = await supabase
        .from('contratacao')
        .select('id, email, cpf')
        .eq('email', userEmail);

      console.log('📊 Contratações encontradas para o email:', { contratacoes, contratacaoError });

      if (contratacaoError) {
        console.error('Erro ao buscar contratações:', contratacaoError);
        toast({
          title: "Erro",
          description: "Erro ao buscar dados de contratação.",
          variant: "destructive"
        });
        setStatusProposta('nao_encontrado');
        return;
      }

      if (!contratacoes || contratacoes.length === 0) {
        console.log('❌ Nenhuma contratação encontrada para o email:', userEmail);
        setStatusProposta('nao_encontrado');
        return;
      }

      // Usar a primeira contratação encontrada para definir o ID
      const contratacao = contratacoes[0];
      console.log('✅ Contratação principal encontrada, ID:', contratacao.id);
      setContratacaoId(contratacao.id);

      // Extrair CPF para busca alternativa
      const cpfNumbers = contratacao.cpf?.replace(/\D/g, '') || '';
      console.log('📊 CPF extraído:', cpfNumbers);

      // Buscar propostas de TODAS as contratações relacionadas ao usuário
      const contratacaoIds = contratacoes.map(c => c.id);
      console.log('🔍 Buscando propostas para todas as contratações do usuário:', contratacaoIds);
      
      // CORREÇÃO: Buscar na tabela correta contratacao_propostas com ordenação
      const { data: todasPropostas, error: errorTodasPropostas } = await supabase
        .from('contratacao_propostas')
        .select('*')
        .in('contratacao_id', contratacaoIds)
        .order('updated_at', { ascending: false });

      console.log('📊 TODAS as propostas das contratações do usuário:', { todasPropostas, errorTodasPropostas });

      // Se não encontrou pelas contratações, tentar buscar pelo CPF no documento
      if (!todasPropostas || todasPropostas.length === 0) {
        console.log('🔍 Tentando buscar por CPF no documento_cpf_nome...');
        
        if (cpfNumbers) {
          // CORREÇÃO: Buscar na tabela correta contratacao_propostas
          const { data: contratosPorCpf, error: errorCpf } = await supabase
            .from('contratacao_propostas')
            .select('*')
            .like('documento_cpf_nome', `%${cpfNumbers}%`)
            .order('updated_at', { ascending: false });
          
          console.log('📊 Propostas encontradas por CPF:', { contratosPorCpf, errorCpf });
          
          if (contratosPorCpf && contratosPorCpf.length > 0) {
            // Verificar se ALGUMA proposta está assinada
            const algumaAssinada = contratosPorCpf.some(p => isAssinado(p.clicksign_assinado));
            
            if (algumaAssinada) {
              console.log('✅ Pelo menos uma proposta está assinada (busca por CPF)');
              setStatusProposta('assinado');
            } else {
              console.log('⏳ Nenhuma proposta assinada (busca por CPF)');
              setStatusProposta('pendente');
            }
            return;
          }
        }

        // Se ainda não encontrou, tentar buscar pelo email diretamente em propostas existentes
        console.log('🔍 Tentando buscar por email no sistema...');
        
        // Buscar qualquer proposta que contenha o email ou CPF no nome do documento
        const emailBase = userEmail.split('@')[0]; // Pegar parte antes do @
        // CORREÇÃO: Buscar na tabela correta contratacao_propostas
        const { data: propostasPorEmail, error: errorEmail } = await supabase
          .from('contratacao_propostas')
          .select('*')
          .or(`documento_cpf_nome.like.%${emailBase}%,documento_cpf_nome.like.%${cpfNumbers}%`)
          .order('updated_at', { ascending: false });
        
        console.log('📊 Propostas encontradas por email/CPF:', { propostasPorEmail, errorEmail });
        
        if (propostasPorEmail && propostasPorEmail.length > 0) {
          // Verificar se ALGUMA proposta está assinada
          const algumaAssinada = propostasPorEmail.some(p => isAssinado(p.clicksign_assinado));
          
          if (algumaAssinada) {
            console.log('✅ Pelo menos uma proposta está assinada (busca por email)');
            setStatusProposta('assinado');
          } else {
            console.log('⏳ Nenhuma proposta assinada (busca por email)');
            setStatusProposta('pendente');
          }
          return;
        }
        
        console.log('❌ Nenhuma proposta encontrada para o usuário');
        setStatusProposta('nao_encontrado');
        return;
      }

      // Verificar se alguma proposta foi encontrada (não precisa mais filtrar)
      if (!todasPropostas || todasPropostas.length === 0) {
        console.log('❌ Nenhuma proposta encontrada nas contratações do usuário');
        setStatusProposta('nao_encontrado');
        return;
      }

      // Verificar se ALGUMA proposta está assinada (não apenas a primeira)
      const algumaAssinada = todasPropostas.some(proposta => isAssinado(proposta.clicksign_assinado));
      console.log('📊 Propostas encontradas:', todasPropostas.length, '| Alguma assinada:', algumaAssinada);
      
      if (algumaAssinada) {
        console.log('✅ Pelo menos uma proposta está assinada');
        setStatusProposta('assinado');
      } else {
        console.log('⏳ Nenhuma proposta assinada encontrada');
        setStatusProposta('pendente');
      }

    } catch (error) {
      console.error('Erro ao verificar status da proposta:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado ao verificar proposta.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvancar = async () => {
    // Atualizar evolução para próxima etapa
    try {
      const { data: evolucaoExistente } = await supabase
        .from('evolucao_contratacao')
        .select('id')
        .eq('user_id', contratacaoId)
        .maybeSingle();

      if (evolucaoExistente) {
        await supabase
          .from('evolucao_contratacao')
          .update({ 
            etapa_atual: 'dados_pessoais',
            etapa_mais_avancada: 'dados_pessoais',
            proposta_status: 'concluido'
          })
          .eq('user_id', contratacaoId);
      } else {
        await supabase
          .from('evolucao_contratacao')
          .insert({
            user_id: contratacaoId,
            etapa_atual: 'dados_pessoais',
            etapa_mais_avancada: 'dados_pessoais',
            proposta_status: 'concluido'
          });
      }
      
      onAvancar();
    } catch (error) {
      console.error('Erro ao avançar etapa:', error);
      toast({
        title: "Erro",
        description: "Erro ao avançar para próxima etapa.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Verificando status da proposta...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold mb-2">Proposta de Trabalho</h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Status da sua proposta de trabalho
          </p>
        </div>

        <Card className="border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {statusProposta === 'assinado' ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>Proposta Assinada</span>
                <Badge variant="default" className="bg-green-100 text-green-800">
                  Concluído
                </Badge>
              </>
            ) : statusProposta === 'pendente' ? (
              <>
                <Clock className="h-5 w-5 text-amber-600" />
                <span>Proposta Enviada</span>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                  Aguardando Assinatura
                </Badge>
              </>
            ) : (
              <>
                <Clock className="h-5 w-5 text-gray-600" />
                <span>Proposta</span>
                <Badge variant="outline">
                  Não Encontrada
                </Badge>
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {statusProposta === 'assinado' ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-800 font-medium">
                    Assinatura da Proposta de Trabalho confirmada!
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    Você está liberado para avançar para a próxima etapa.
                  </p>
                </div>
              </div>
            </div>
          ) : statusProposta === 'pendente' ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-800 font-medium">
                    A sua Proposta de Trabalho foi enviada para o seu e-mail.
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    Verifique sua caixa de entrada e também a de spam para assiná-la. 
                    Após a assinatura ser confirmada, a próxima etapa será liberada automaticamente.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-800 font-medium">
                    Nenhuma proposta encontrada para este usuário.
                  </p>
                  <p className="text-gray-700 text-sm mt-1">
                    Entre em contato com o RH para verificar a situação.
                  </p>
                </div>
              </div>
            </div>
          )}

          {statusProposta === 'pendente' && (
            <VerificarStatusClicksign 
              onVerificacaoCompleta={() => {
                // Recarregar status após verificação
                setTimeout(() => {
                  buscarContratacaoEVerificarProposta();
                }, 2000);
              }} 
            />
          )}

          {statusProposta === 'assinado' && (
            <div className="flex justify-end pt-4">
              <Button onClick={handleAvancar} size="lg">
                Avançar para Próxima Etapa
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      <PropostasEnviadasDisplay userEmail={userEmail} />
    </div>
  );
}